package ai.wagemix.logic.payment

import ai.wagemix.data.WMBillingSyncEvent
import ai.wagemix.logic.isGlobal
import ai.wagemix.logic.payment.WMPaymentResultPresenter.Action
import ai.wagemix.logic.payment.WMPaymentResultPresenter.Event
import ai.wagemix.net.models.WMCheckoutSummary
import com.fsryan.foundation.logic.FSBasePresenter
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.models.FSPropertyContainer
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.onEach

/** The page Stripe returns to: the plan, the billing cycle and the next payment date of the session. */
interface WMPaymentResultPresenter<out E: Event, in A: Action>: FSPresenter<E, A> {

    interface Action: FSPresenter.Action {
        sealed interface Base: Action

        @ConsistentCopyVisibility
        data class Retry internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<Retry> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<out A: Action>: FSPresenter.Action.Factory<A> {
            fun createRetry(extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createRetry(extendedProperties: Map<String, Any>): Base = Retry(extendedProperties)
            }
        }
    }

    interface Event: FSPresenter.Event {
        val summaryUpdate: WMCheckoutSummary?
        /** `true` when the session could not be read (no id, or the callable failed). */
        val failedUpdate: Boolean?

        @ConsistentCopyVisibility
        data class Base internal constructor(
            private val base: FSPresenter.Event.Base,
            override val summaryUpdate: WMCheckoutSummary? = null,
            override val failedUpdate: Boolean? = null
        ): Event, FSPropertyContainer<Base> {
            override val loadingUpdate: Boolean? get() = base.loadingUpdate
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<out E: Event>: FSPresenter.Event.Factory<E> {
            fun createSummaryUpdate(summary: WMCheckoutSummary): E
            fun createFailed(failed: Boolean): E
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                private val base = FSPresenter.Event.baseFactory()
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createSummaryUpdate(summary: WMCheckoutSummary) = Base(base.none(), summaryUpdate = summary)
                override fun createFailed(failed: Boolean) = Base(base.none(), failedUpdate = failed)
            }
        }
    }

    fun userRequestedRetry(extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBasePaymentResultPresenter<
    E: Event,
    A: Action,
    EF: Event.Factory<E>,
    AF: Action.Factory<A>,
    G: WMPaymentResultDataGateway
>(
    protected val sessionId: String?,
    protected val dataGateway: G,
    eventFactory: EF,
    protected val actionFactory: AF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String
): FSBasePresenter<E, A, EF>(cancelViewScopeOnUnready, instanceId, eventFactory), WMPaymentResultPresenter<E, A> {

    override fun userRequestedRetry(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createRetry(extendedProperties))

    override fun registerSubscriptions() {
        val id = sessionId ?: return
        dataGateway.contextUpdateFlow
            .filter { it.isGlobal && it.checkoutSummaryChanged(id) }
            .onEach { update -> update.after.checkoutSummary(id)?.let { eventFactory.createSummaryUpdate(it).emit() } }
            .launchInViewScope()

        dataGateway.billingSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMBillingSyncEvent.FetchCheckoutSummary.Initial -> {
                        // A retry starts over: the failure of the previous attempt no longer holds.
                        eventFactory.createFailed(false).emit()
                        eventFactory.createLoadingChange(true).emit()
                    }
                    is WMBillingSyncEvent.FetchCheckoutSummary.Success -> {
                        // `checkout_completed` is the API's, from Stripe's webhook (analytics plan §5a).
                        eventFactory.createLoadingChange(false).emit()
                    }
                    is WMBillingSyncEvent.FetchCheckoutSummary.Failure -> {
                        eventFactory.createLoadingChange(false).emit()
                        eventFactory.createFailed(true).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        val id = sessionId ?: return eventFactory.createFailed(true).emit()
        eventFactory.createFailed(false).emit()
        when (val known = dataGateway.retrieveGlobalContext().checkoutSummary(id)) {
            null -> dataGateway.fetchCheckoutSummary(id, createPresenterContext())
            else -> eventFactory.createSummaryUpdate(known).emit()
        }
    }

    override fun onUserAction(action: A) {
        logUserAction(action)
        when (action) {
            is Action.Retry -> sessionId?.let { dataGateway.fetchCheckoutSummary(it, createPresenterContext(action)) }
            else -> logUnknownActionType(action)
        }
    }
}

typealias WMDefaultPaymentResultPresenter = WMPaymentResultPresenter<Event.Base, Action.Base>

fun WMPaymentResultPresenter.Companion.createDefault(
    sessionId: String?,
    dataGateway: WMPaymentResultDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMPaymentResultPresenter")
): WMDefaultPaymentResultPresenter = object: WMBasePaymentResultPresenter<Event.Base, Action.Base, Event.Factory<Event.Base>, Action.Factory<Action.Base>, WMPaymentResultDataGateway>(
    sessionId = sessionId,
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}
